import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Users } from "lucide-react";
import type { AuditErrorSummaryQuery } from "@nh/shared";
import { Alert, Badge, Input } from "@/shared/ui/core";
import { EmptyState, SkeletonTable } from "@/shared/ui/ui";
import { cn } from "@/shared/lib/cn";
import {
  DataTable,
  FilterBar,
  KpiCard,
  Pagination,
  Td,
  Th,
  Tr,
  DEFAULT_PER_PAGE,
} from "@/features/admin/components";
import { getErrorSummary } from "./audit.api";
import { dayEndIso, dayStartIso, formatAuditDateTime, statusVariant } from "./format";

const PRESETS = [
  { key: "3h" as const, label: "3 h", hours: 3 },
  { key: "24h" as const, label: "24 h", hours: 24 },
  { key: "7d" as const, label: "7 días", hours: 24 * 7 },
];

type PresetKey = (typeof PRESETS)[number]["key"];

/**
 * El inicio de un atajo, calculado UNA vez al elegirlo: si se calculara
 * en cada render, la llave de la consulta cambiaría a cada milisegundo y
 * la pestaña no dejaría de pedir.
 */
function hoursAgoIso(hours: number): string {
  return new Date(Date.now() - hours * 3_600_000).toISOString();
}

export interface AuditErrorsTabProps {
  /** Lleva a Movimientos filtrado por esa ruta y por «error». */
  onOpenGroup: (path: string) => void;
}

/**
 * Errores — «¿qué se está rompiendo y a quién?». Los errores del periodo
 * agrupados por ruta, estado y motivo. Por defecto, las últimas 24 h.
 */
export function AuditErrorsTab({ onOpenGroup }: AuditErrorsTabProps) {
  const { t } = useTranslation();
  const [preset, setPreset] = useState<PresetKey | "custom">("24h");
  const [presetFrom, setPresetFrom] = useState(() => hoursAgoIso(24));
  const [fromDay, setFromDay] = useState("");
  const [toDay, setToDay] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(DEFAULT_PER_PAGE);

  const customFrom = dayStartIso(fromDay);
  const customTo = dayEndIso(toDay);
  const query: AuditErrorSummaryQuery =
    preset === "custom"
      ? { ...(customFrom ? { from: customFrom } : {}), ...(customTo ? { to: customTo } : {}) }
      : { from: presetFrom };

  const summary = useQuery({
    queryKey: ["admin", "audit", "errors", query],
    queryFn: () => getErrorSummary(query),
    placeholderData: (previous) => previous,
  });

  const groups = summary.data?.groups ?? [];
  // Los grupos llegan completos: se paginan aquí, nunca una lista sin fin.
  const visible = groups.slice((page - 1) * perPage, page * perPage);

  function elegirAtajo(key: PresetKey, hours: number) {
    setPreset(key);
    setPresetFrom(hoursAgoIso(hours));
    setFromDay("");
    setToDay("");
    setPage(1);
  }

  function elegirDia(which: "from" | "to", value: string) {
    if (which === "from") setFromDay(value);
    else setToDay(value);
    setPreset("custom");
    setPage(1);
  }

  return (
    <div aria-busy={summary.isFetching}>
      <FilterBar>
        <div className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
          <span id="audit-errors-preset">{t("Periodo")}</span>
          <div
            role="group"
            aria-labelledby="audit-errors-preset"
            className="inline-flex rounded-full border border-ds-border bg-white p-1"
          >
            {PRESETS.map((item) => (
              <button
                key={item.key}
                type="button"
                aria-pressed={preset === item.key}
                onClick={() => elegirAtajo(item.key, item.hours)}
                className={cn(
                  "rounded-full px-3.5 py-1.5 text-[13px] font-semibold transition",
                  preset === item.key
                    ? "bg-ds-violet-pop text-white hover:bg-ds-violet-pop hover:text-white"
                    : "text-ds-ink-3 hover:bg-ds-mist hover:text-ds-ink",
                )}
              >
                {t(item.label)}
              </button>
            ))}
          </div>
        </div>
        <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
          {t("Desde")}
          <Input type="date" value={fromDay} onChange={(event) => elegirDia("from", event.target.value)} />
        </label>
        <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
          {t("Hasta")}
          <Input type="date" value={toDay} onChange={(event) => elegirDia("to", event.target.value)} />
        </label>
      </FilterBar>

      {summary.data && (
        <div className="grid gap-3 border-b border-ds-border p-4 [grid-template-columns:repeat(auto-fit,minmax(170px,1fr))]">
          <KpiCard
            icon={AlertTriangle}
            label={t("Errores")}
            value={String(summary.data.total)}
            hint={`${formatAuditDateTime(summary.data.from)} – ${formatAuditDateTime(summary.data.to)}`}
          />
          <KpiCard icon={Users} label={t("Personas afectadas")} value={String(summary.data.affected)} />
        </div>
      )}

      {summary.isError ? (
        <Alert variant="error" className="m-4">
          {t("No se pudo cargar el resumen de errores. Inténtalo otra vez.")}
        </Alert>
      ) : summary.isPending ? (
        <div className="p-5">
          <SkeletonTable rows={5} columns={6} />
        </div>
      ) : groups.length === 0 ? (
        <div className="p-4">
          <EmptyState
            title={t("Sin errores en este periodo")}
            description={t("Nada se rompió. Amplía el periodo para ver más atrás.")}
          />
        </div>
      ) : (
        <DataTable caption={t("Errores agrupados")}>
          <thead>
            <tr>
              <Th>{t("Petición")}</Th>
              <Th align="center" className="w-[9%]">{t("Estado")}</Th>
              <Th className="w-[30%]">{t("Motivo")}</Th>
              <Th align="right" className="w-[8%]">{t("Veces")}</Th>
              <Th align="right" className="w-[9%]">{t("Usuarios")}</Th>
              <Th align="right" className="w-[16%] whitespace-nowrap">{t("Última vez")}</Th>
            </tr>
          </thead>
          <tbody>
            {visible.map((group) => (
              <Tr
                key={`${group.kind}|${group.method}|${group.path}|${group.status}|${group.errorCode ?? ""}`}
                tabIndex={0}
                aria-label={t("Ver los movimientos de {{path}}", { path: group.path })}
                onClick={() => onOpenGroup(group.path)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onOpenGroup(group.path);
                  }
                }}
                className="cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ds-violet-pop"
              >
                <Td>
                  <p className="break-all font-mono text-[12.5px]">
                    {group.method} {group.path}
                  </p>
                  {group.kind === "client" && (
                    <p className="text-[12px] text-ds-ink-3">{t("Navegador")}</p>
                  )}
                </Td>
                <Td align="center">
                  <Badge variant={statusVariant(group.status)} size="sm">
                    {group.status}
                  </Badge>
                </Td>
                <Td>
                  {group.errorCode && (
                    <p className="font-mono text-[12px] text-ds-ink">{group.errorCode}</p>
                  )}
                  {group.errorMessage && (
                    <p className="text-[12.5px] text-ds-ink-3">{group.errorMessage}</p>
                  )}
                  {!group.errorCode && !group.errorMessage && <span className="text-ds-ink-3">—</span>}
                </Td>
                <Td align="right" className="font-medium">
                  {group.count}
                </Td>
                <Td align="right" title={group.users.join(", ") || undefined}>
                  {group.users.length}
                </Td>
                <Td align="right" className="whitespace-nowrap text-[13px] text-ds-ink-3">
                  {formatAuditDateTime(group.lastAt)}
                </Td>
              </Tr>
            ))}
          </tbody>
        </DataTable>
      )}

      {groups.length > 0 && (
        <Pagination
          page={page}
          perPage={perPage}
          total={groups.length}
          onPageChange={setPage}
          onPerPageChange={(n) => {
            setPerPage(n);
            setPage(1);
          }}
        />
      )}
    </div>
  );
}
